// src/pages/AdminPage/ScenariosImportTab.tsx
// Импорт сценариев из JSON: ввод → валидация/превью → импорт с прогрессом → итог.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  LinearProgress,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Upload,
  FileJson,
  CheckCircle2,
  XCircle,
  Download,
  RotateCcw,
  Play,
  Square,
  ArrowLeft,
  AlertTriangle,
  Sparkles,
  Users,
  Copy,
  Check,
  ChevronDown,
  ClipboardList,
} from 'lucide-react';
import { createScenario, listRoles, type BackendRole } from 'services/scenariosApi';
import {
  parseScenariosJson,
  validateItem,
  toPayload,
  scenarioTitle,
  applyTestSuffix,
  buildAiPrompt,
  TEST_TITLE_SUFFIX,
  SCENARIOS_IMPORT_TEMPLATE,
  type RawScenario,
} from 'utils/scenariosImport';

type Phase = 'input' | 'preview' | 'running' | 'done';

interface PreviewRow {
  item: RawScenario;
  index: number;
  title: string;
  roleName: string | null;
  errors: string[];
  valid: boolean;
}

interface ResultRow {
  index: number;
  title: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

const muted = 'var(--color-text-muted)';
const primary = 'var(--color-primary)';

function downloadFile(name: string, content: string): void {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Кнопка «Скопировать» с временным состоянием «Скопировано». */
function CopyButton({
  text,
  label = 'Скопировать',
  size = 14,
}: {
  text: string;
  label?: string;
  size?: number;
}) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return (
    <Button
      size="small"
      variant="text"
      onClick={copy}
      startIcon={copied ? <Check size={size} /> : <Copy size={size} />}
      sx={{ fontSize: 12, textTransform: 'none', color: copied ? '#2e7d32' : primary, minWidth: 0 }}
    >
      {copied ? 'Скопировано' : label}
    </Button>
  );
}

/** Карточка роли: имя + кликабельный UUID (копирует role_id, показывает галочку). */
function RoleChip({ id, name }: { id: string; name: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard?.writeText(id).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [id]);

  return (
    <Box
      sx={{
        border: '1px solid var(--color-border)',
        borderRadius: '9px',
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        background: 'var(--color-bg-elevated, #fff)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{ width: 7, height: 7, borderRadius: '50%', background: primary, flexShrink: 0 }}
        />
        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>
          {name}
        </Typography>
      </Box>
      <Tooltip title={copied ? 'Скопировано' : 'Нажмите, чтобы скопировать role_id'}>
        <Box
          component="button"
          onClick={copy}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            width: '100%',
            border: '1px solid',
            borderColor: copied ? '#2e7d32' : 'var(--color-border)',
            borderRadius: '6px',
            px: 0.75,
            py: 0.4,
            cursor: 'pointer',
            background: copied ? 'rgba(46,125,50,0.06)' : 'rgba(16,24,40,0.02)',
            textAlign: 'left',
            transition: 'all 0.12s ease',
            '&:hover': { borderColor: primary, background: 'rgba(20,116,255,0.05)' },
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontFamily: 'monospace',
              color: muted,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {id}
          </Typography>
          {copied ? <Check size={13} color="#2e7d32" /> : <Copy size={13} color={muted} />}
        </Box>
      </Tooltip>
    </Box>
  );
}

/** Сворачиваемая секция инструкции. */
function Section({
  icon,
  title,
  defaultOpen = false,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box
      sx={{
        borderBottom: '1px solid var(--color-border)',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { background: 'rgba(20,116,255,0.03)' },
        }}
      >
        {icon}
        <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{title}</Typography>
        <ChevronDown
          size={16}
          color={muted}
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.75, pb: 1.75 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

export default function ScenariosImportTab() {
  const [phase, setPhase] = useState<Phase>('input');
  const [raw, setRaw] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const [roleMap, setRoleMap] = useState<Map<string, string>>(new Map());
  const abortRef = useRef(false);

  useEffect(() => {
    listRoles().then((roles?: BackendRole[]) => {
      if (!roles) return;
      const m = new Map<string, string>();
      roles.forEach((r) => m.set(r.id, r.title || r.name || r.id));
      setRoleMap(m);
    });
  }, []);

  const knownRoleIds = useMemo(() => new Set(roleMap.keys()), [roleMap]);
  const rolesList = useMemo(
    () => Array.from(roleMap.entries()).map(([id, name]) => ({ id, name })),
    [roleMap]
  );
  const aiPrompt = useMemo(() => buildAiPrompt(rolesList), [rolesList]);
  // Шаблон с подставленным реальным role_id (первая роль), если роли загружены.
  const templateJson = useMemo(() => {
    const firstId = rolesList[0]?.id;
    return firstId
      ? SCENARIOS_IMPORT_TEMPLATE.replace(/ЗАМЕНИТЕ-НА-UUID-РОЛИ/g, firstId)
      : SCENARIOS_IMPORT_TEMPLATE;
  }, [rolesList]);

  // ─── Ввод ────────────────────────────────────────────────────────────────
  const readFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = () => setRaw(String(reader.result ?? ''));
    reader.readAsText(file);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) readFile(file);
    },
    [readFile]
  );

  const onPickFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) readFile(file);
      e.target.value = '';
    },
    [readFile]
  );

  const buildPreview = useCallback(
    (items: RawScenario[]): PreviewRow[] =>
      items.map((item, index) => {
        const { ok, errors } = validateItem(item, knownRoleIds);
        const roleId = typeof item.role_id === 'string' ? item.role_id : '';
        return {
          item,
          index,
          title: applyTestSuffix(scenarioTitle(item, index)),
          roleName: roleMap.get(roleId) ?? null,
          errors,
          valid: ok,
        };
      }),
    [knownRoleIds, roleMap]
  );

  const handleValidate = useCallback(() => {
    const { items, error } = parseScenariosJson(raw);
    if (error || !items) {
      setParseError(error);
      return;
    }
    setParseError(null);
    setRows(buildPreview(items));
    setPhase('preview');
  }, [raw, buildPreview]);

  // ─── Импорт ──────────────────────────────────────────────────────────────
  const runImport = useCallback(async (toImport: PreviewRow[]) => {
    abortRef.current = false;
    setPhase('running');
    const initial: ResultRow[] = toImport.map((r) => ({
      index: r.index,
      title: r.title,
      status: 'pending',
    }));
    setResults(initial);

    for (let i = 0; i < toImport.length; i += 1) {
      if (abortRef.current) break;
      const row = toImport[i];
      try {
        await createScenario(toPayload(row.item));
        setResults((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'success' } : r)));
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Неизвестная ошибка';
        setResults((prev) =>
          prev.map((r, idx) => (idx === i ? { ...r, status: 'error', error: msg } : r))
        );
      }
    }
    setPhase('done');
  }, []);

  const validRows = useMemo(() => rows.filter((r) => r.valid), [rows]);
  const invalidCount = rows.length - validRows.length;

  const done = results.filter((r) => r.status !== 'pending').length;
  const succeeded = results.filter((r) => r.status === 'success').length;
  const failedResults = results.filter((r) => r.status === 'error');
  const progressPct = results.length ? Math.round((done / results.length) * 100) : 0;
  const currentTitle = results.find((r) => r.status === 'pending')?.title ?? '';

  const retryFailed = useCallback(() => {
    const failedRows = failedResults
      .map((f) => rows.find((r) => r.index === f.index))
      .filter((r): r is PreviewRow => Boolean(r));
    if (failedRows.length) runImport(failedRows);
  }, [failedResults, rows, runImport]);

  const reset = useCallback(() => {
    abortRef.current = false;
    setRaw('');
    setRows([]);
    setResults([]);
    setParseError(null);
    setPhase('input');
  }, []);

  const downloadErrorReport = useCallback(() => {
    const report = failedResults.map((f) => ({ title: f.title, error: f.error }));
    downloadFile('scenarios-import-errors.json', JSON.stringify(report, null, 2));
  }, [failedResults]);

  // ─── Рендер ────────────────────────────────────────────────────────────────
  // Карточка-инструкция (роли / генерация через ИИ / формат) — общая для ввода и превью.
  const instructions = (
    <Box
      sx={{
        border: '1px solid var(--color-border)',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'var(--color-bg-elevated, #fff)',
        boxShadow: '0 1px 3px rgba(16,24,40,0.04)',
      }}
    >
      {/* Доступные роли */}
      <Section
        icon={<Users size={15} color={primary} />}
        title={`Доступные роли${rolesList.length ? ` (${rolesList.length})` : ''}`}
        defaultOpen
      >
        {rolesList.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: muted }}>
            Роли загружаются… либо не настроены. Укажите <code>role_id</code> вручную.
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 11.5, color: muted, mb: 1 }}>
              Используйте <code>role_id</code> из карточек ниже. Нажмите на UUID, чтобы скопировать.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 1,
              }}
            >
              {rolesList.map((r) => (
                <RoleChip key={r.id} id={r.id} name={r.name} />
              ))}
            </Box>
          </>
        )}
      </Section>

      {/* Генерация через ИИ */}
      <Section icon={<Sparkles size={15} color={primary} />} title="Сгенерировать через ИИ">
        <Typography sx={{ fontSize: 12.5, color: muted, mb: 1, lineHeight: 1.6 }}>
          Скопируйте промпт ниже, вставьте его в любой ИИ (ChatGPT, Claude, GigaChat), допишите в
          конце тему сценариев — и вставьте полученный JSON в поле ввода. Список ролей уже
          подставлен.
        </Typography>
        <Box
          sx={{
            position: 'relative',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            p: 1.25,
            maxHeight: 220,
            overflowY: 'auto',
            background: 'rgba(16,24,40,0.02)',
          }}
        >
          <Typography
            component="pre"
            sx={{
              fontSize: 11.5,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              m: 0,
              color: 'var(--color-text-primary)',
            }}
          >
            {aiPrompt}
          </Typography>
        </Box>
        <Box sx={{ mt: 0.75 }}>
          <CopyButton text={aiPrompt} label="Скопировать промпт" />
        </Box>
      </Section>

      {/* Формат JSON */}
      <Section icon={<ClipboardList size={15} color={primary} />} title="Формат JSON">
        <Typography sx={{ fontSize: 12.5, color: muted, mb: 1, lineHeight: 1.6 }}>
          Массив объектов. Обязательные: <code>role_id</code> (UUID из списка ролей) и{' '}
          <code>title</code>. Шаги — массивом <code>steps</code> либо строкой <code>action</code>.
          Опционально: <code>description</code>, <code>result</code>, <code>instruction</code>,{' '}
          <code>system_prompt</code>, <code>model</code>, <code>min_files</code>,{' '}
          <code>max_files</code>, <code>ordering</code>.
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<Download size={14} />}
            onClick={() => downloadFile('scenarios-template.json', templateJson)}
            sx={{ fontSize: 12, textTransform: 'none' }}
          >
            Скачать шаблон
          </Button>
          <CopyButton text={templateJson} label="Скопировать пример" />
        </Box>
      </Section>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Заголовок раздела */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(20,116,255,0.1)',
            flexShrink: 0,
          }}
        >
          <FileJson size={17} color={primary} />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>
          Импорт сценариев из JSON
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: muted, mb: 2.25, lineHeight: 1.6 }}>
        Загрузите массив сценариев — система создаст их пачкой через API. К названию каждого
        автоматически добавится суффикс <b>«{TEST_TITLE_SUFFIX.trim()}»</b>.
      </Typography>

      {/* ФАЗА: ВВОД — инструкция слева, поле ввода справа (2 колонки на широких экранах) */}
      {phase === 'input' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {instructions}

          <Box>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              sx={{
                border: `1.5px dashed ${dragOver ? primary : 'var(--color-border)'}`,
                borderRadius: '10px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.15s ease',
                background: dragOver ? 'rgba(20,116,255,0.05)' : 'transparent',
                mb: 2,
              }}
            >
              <Upload size={22} color={muted} style={{ marginBottom: 6 }} />
              <Typography sx={{ fontSize: 13, color: muted, mb: 1 }}>
                Перетащите .json сюда или
              </Typography>
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Выбрать файл
                <input type="file" accept=".json,application/json" hidden onChange={onPickFile} />
              </Button>
            </Box>

            <TextField
              multiline
              minRows={12}
              maxRows={26}
              fullWidth
              placeholder='[{ "role_id": "...", "title": "..." }]'
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'monospace',
                  fontSize: 12.5,
                },
              }}
            />

            {parseError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <XCircle size={15} color="#d32f2f" />
                <Typography sx={{ fontSize: 12.5, color: '#d32f2f' }}>{parseError}</Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<CheckCircle2 size={16} />}
                onClick={handleValidate}
                sx={{ textTransform: 'none', px: 2.5 }}
              >
                Проверить
              </Button>
              <Typography sx={{ fontSize: 12, color: muted }}>
                {raw.trim()
                  ? 'Проверим JSON и покажем превью перед импортом'
                  : 'Вставьте JSON или загрузите файл'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ФАЗА: ПРЕВЬЮ */}
      {phase === 'preview' && (
        <>
          <Box sx={{ mb: 2.5 }}>{instructions}</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              Найдено сценариев: {rows.length}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle2 size={14} color="#2e7d32" />
              <Typography sx={{ fontSize: 12.5, color: '#2e7d32' }}>
                валидных: {validRows.length}
              </Typography>
            </Box>
            {invalidCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AlertTriangle size={14} color="#ed6c02" />
                <Typography sx={{ fontSize: 12.5, color: '#ed6c02' }}>
                  с ошибками: {invalidCount} (будут пропущены)
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ border: '1px solid var(--color-border)', borderRadius: '10px', mb: 2 }}>
            {rows.map((r) => (
              <Box
                key={r.index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  px: 1.75,
                  py: 1.25,
                  borderBottom: '1px solid var(--color-border)',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                {r.valid ? (
                  <CheckCircle2 size={16} color="#2e7d32" style={{ marginTop: 2, flexShrink: 0 }} />
                ) : (
                  <XCircle size={16} color="#d32f2f" style={{ marginTop: 2, flexShrink: 0 }} />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.title}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: muted }}>
                    Роль:{' '}
                    {r.roleName ?? (
                      <span style={{ color: '#d32f2f' }}>
                        {typeof r.item.role_id === 'string' ? r.item.role_id : '—'}
                      </span>
                    )}
                    {Array.isArray(r.item.steps) ? ` · шагов: ${r.item.steps.length}` : ''}
                  </Typography>
                  {r.errors.map((err, i) => (
                    <Typography key={i} sx={{ fontSize: 11.5, color: '#d32f2f' }}>
                      • {err}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={15} />}
              onClick={() => setPhase('input')}
              sx={{ textTransform: 'none' }}
            >
              Назад
            </Button>
            <Tooltip title={validRows.length === 0 ? 'Нет валидных сценариев' : ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Play size={15} />}
                  disabled={validRows.length === 0}
                  onClick={() => runImport(validRows)}
                  sx={{ textTransform: 'none' }}
                >
                  Импортировать {validRows.length}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </>
      )}

      {/* ФАЗА: ИМПОРТ */}
      {phase === 'running' && (
        <>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              Импорт… {done} / {results.length}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Square size={13} />}
              onClick={() => {
                abortRef.current = true;
              }}
              sx={{ textTransform: 'none' }}
            >
              Остановить
            </Button>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{ borderRadius: 2, mb: 0.75 }}
          />
          <Typography sx={{ fontSize: 12, color: muted, mb: 2, minHeight: 18 }}>
            {currentTitle ? `Создаётся: ${currentTitle}` : 'Завершение…'}
          </Typography>
          <ImportLog results={results} />
        </>
      )}

      {/* ФАЗА: ИТОГ */}
      {phase === 'done' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CheckCircle2 size={18} color="#2e7d32" />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#2e7d32' }}>
                Создано: {succeeded}
              </Typography>
            </Box>
            {failedResults.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <XCircle size={18} color="#d32f2f" />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#d32f2f' }}>
                  Ошибок: {failedResults.length}
                </Typography>
              </Box>
            )}
          </Box>

          <ImportLog results={results} />

          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            {failedResults.length > 0 && (
              <>
                <Button
                  variant="contained"
                  startIcon={<RotateCcw size={15} />}
                  onClick={retryFailed}
                  sx={{ textTransform: 'none' }}
                >
                  Повторить сбойные ({failedResults.length})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Download size={15} />}
                  onClick={downloadErrorReport}
                  sx={{ textTransform: 'none' }}
                >
                  Скачать отчёт
                </Button>
              </>
            )}
            <Button variant="text" onClick={reset} sx={{ textTransform: 'none' }}>
              Новый импорт
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

function ImportLog({ results }: { results: ResultRow[] }) {
  return (
    <Box
      sx={{
        border: '1px solid var(--color-border)',
        borderRadius: '10px',
        maxHeight: 320,
        overflowY: 'auto',
      }}
    >
      {results.map((r, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.75,
            py: 1,
            borderBottom: '1px solid var(--color-border)',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          {r.status === 'success' && <CheckCircle2 size={15} color="#2e7d32" />}
          {r.status === 'error' && <XCircle size={15} color="#d32f2f" />}
          {r.status === 'pending' && (
            <Box
              sx={{
                width: 15,
                height: 15,
                borderRadius: '50%',
                border: '2px solid var(--color-border)',
              }}
            />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 500 }} noWrap>
              {r.title}
            </Typography>
            {r.error && (
              <Typography sx={{ fontSize: 11.5, color: '#d32f2f' }}>{r.error}</Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
